/**
 * useMiniApp — Farcaster Mini-App SDK integration hook
 *
 * Detects if the app is running inside a Farcaster client (Base app),
 * initialises the SDK, and exposes context + actions for the rest of the app.
 *
 * CW-501: Sprint 2 — Base Mini-App integration
 */
import { useEffect, useState, useCallback } from "react";

// Lazy-import the SDK so the bundle doesn't break in non-Farcaster contexts
let sdk: any = null;

interface MiniAppContext {
  /** Whether the app is running inside a Farcaster mini-app frame */
  isMiniApp: boolean;
  /** Whether the SDK has finished initialising */
  isReady: boolean;
  /** Farcaster user context (fid, username, pfp, etc.) if available */
  user: any | null;
  /** Share a cast from within the mini-app */
  shareCast: (text: string) => Promise<void>;
  /** Open an external URL via the host app */
  openUrl: (url: string) => Promise<void>;
  /** Close the mini-app */
  close: () => Promise<void>;
}

/**
 * Detect if we're inside a Farcaster mini-app iframe.
 * The host injects `window.parent !== window` and the SDK checks for
 * specific message channels, but a simple heuristic is the presence of
 * the `fc-` query params or being in an iframe.
 */
function detectMiniAppContext(): boolean {
  if (typeof window === "undefined") return false;
  // Check for Farcaster-specific URL params
  const params = new URLSearchParams(window.location.search);
  if (params.has("fc_action") || params.has("fc_context")) return true;
  // Check if we're in an iframe (common for mini-apps)
  try {
    return window.self !== window.top;
  } catch {
    return true; // Cross-origin iframe
  }
}

export function useMiniApp(enabled = true): MiniAppContext {
  const [isMiniApp, setIsMiniApp] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<any | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsMiniApp(false);
      setIsReady(false);
      setUser(null);
      return;
    }

    let cancelled = false;

    async function init() {
      const detected = detectMiniAppContext();
      if (!detected) {
        setIsMiniApp(false);
        setIsReady(true); // Ready even if not in mini-app context
        return;
      }

      try {
        // Dynamic import so the SDK only loads when needed
        const mod = await import("@farcaster/miniapp-sdk");
        sdk = mod.sdk;

        // Signal to the host that we're ready (hides splash screen)
        await sdk.actions.ready();

        // Try to get user context
        const context = await sdk.context;
        if (!cancelled && context?.user) {
          setUser(context.user);
        }

        if (!cancelled) {
          setIsMiniApp(true);
          setIsReady(true);
        }
      } catch (err) {
        console.warn("[MiniApp] SDK init failed — running in standalone mode", err);
        if (!cancelled) {
          setIsMiniApp(false);
          setIsReady(true);
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const shareCast = useCallback(async (text: string) => {
    if (!sdk) return;
    try {
      await sdk.actions.composeCast({ text });
    } catch (err) {
      console.warn("[MiniApp] shareCast failed", err);
    }
  }, []);

  const openUrl = useCallback(async (url: string) => {
    if (!sdk) {
      window.open(url, "_blank");
      return;
    }
    try {
      await sdk.actions.openUrl(url);
    } catch {
      window.open(url, "_blank");
    }
  }, []);

  const close = useCallback(async () => {
    if (!sdk) return;
    try {
      await sdk.actions.close();
    } catch (err) {
      console.warn("[MiniApp] close failed", err);
    }
  }, []);

  return { isMiniApp, isReady, user, shareCast, openUrl, close };
}
